// Origo Brand Studio (client) - pick the intro/outro/thumbnail template that
// every generated highlight uses, plus one-off preview renders.
//
// Layout: sidebar with 4 kind tabs, main pane lists templates + defaults +
// preview.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  brandOutputURL,
  getBrandDefaults,
  getBrandRender,
  getBrandTemplate,
  listBrandTemplates,
  putBrandDefaults,
  queueBrandRender,
  type BrandDefaults,
  type BrandField,
  type BrandKind,
  type BrandTemplate,
} from '../api/brand';
import { listMatches } from '../api/portal';

const KIND_TABS: { value: BrandKind; label: string; hint: string; defaultsKey: keyof BrandDefaults }[] = [
  {
    value: 'intro',
    label: 'Intro',
    hint: 'Plays at the start of every generated highlight.',
    defaultsKey: 'default_intro_template_id',
  },
  {
    value: 'outro',
    label: 'Outro',
    hint: 'Plays at the end. Great for a "watch more" call to action.',
    defaultsKey: 'default_outro_template_id',
  },
  {
    value: 'thumbnail',
    label: 'Thumbnail',
    hint: 'JPG used as the video poster on your site + player.',
    defaultsKey: 'default_thumb_template_id',
  },
  {
    value: 'graphic',
    label: 'Graphic',
    hint: 'PNG social cards (Instagram / X). One-off renders only.',
    defaultsKey: 'default_intro_template_id', // graphic has no "default"
  },
];

export function BrandStudioPage() {
  const [kind, setKind] = useState<BrandKind>('intro');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['brand-templates', kind],
    queryFn: () => listBrandTemplates(kind),
    staleTime: 15_000,
  });
  const defaults = useQuery({
    queryKey: ['brand-defaults'],
    queryFn: () => getBrandDefaults(),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!selectedTemplateId && list.data && list.data.length > 0) {
      setSelectedTemplateId(list.data[0].id);
    }
  }, [list.data, selectedTemplateId]);

  useEffect(() => {
    // Reset selection when switching kinds.
    setSelectedTemplateId(null);
  }, [kind]);

  const activeTab = KIND_TABS.find((t) => t.value === kind) ?? KIND_TABS[0];

  return (
    <div className="p-4">
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-slate-900">Brand Studio</h1>
        <p className="text-sm text-slate-500">
          Pick the templates that Origo stamps onto every highlight your rules generate.
          Uploads and layer mapping are handled by our team - reach out if you want to add a new
          template.
        </p>
      </header>

      <nav className="mb-3 flex gap-1 border-b border-slate-200">
        {KIND_TABS.map((t) => (
          <button
            key={t.value}
            className={`px-3 py-1.5 text-sm font-medium transition ${
              kind === t.value
                ? 'border-b-2 border-slate-900 text-slate-900'
                : 'text-slate-500 hover:text-slate-800'
            }`}
            onClick={() => setKind(t.value)}
            title={t.hint}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <p className="mb-3 text-xs text-slate-500">{activeTab.hint}</p>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        <aside>
          <TemplateList
            templates={list.data ?? []}
            loading={list.isLoading}
            selectedId={selectedTemplateId}
            onSelect={setSelectedTemplateId}
            defaultId={defaults.data?.[activeTab.defaultsKey] as string | null | undefined}
            kind={kind}
          />
        </aside>
        <main className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          {selectedTemplateId ? (
            <TemplatePanel
              key={selectedTemplateId}
              templateId={selectedTemplateId}
              kind={kind}
              isDefault={defaults.data?.[activeTab.defaultsKey] === selectedTemplateId}
            />
          ) : (
            <div className="p-6 text-sm text-slate-500">
              {list.isLoading ? 'Loading templates…' : 'No templates available for this kind yet.'}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function TemplateList(props: {
  templates: BrandTemplate[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  defaultId: string | null | undefined;
  kind: BrandKind;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
      {props.loading ? (
        <div className="p-2 text-xs text-slate-400">Loading…</div>
      ) : props.templates.length === 0 ? (
        <div className="p-2 text-xs text-slate-400">No templates yet.</div>
      ) : (
        <ul className="space-y-1">
          {props.templates.map((t) => {
            const isSelected = props.selectedId === t.id;
            const isDefault = props.defaultId === t.id;
            return (
              <li key={t.id}>
                <button
                  className={`w-full rounded px-2 py-1.5 text-left text-sm ${
                    isSelected ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
                  }`}
                  onClick={() => props.onSelect(t.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{t.name}</span>
                    {isDefault && (
                      <span
                        className={`rounded px-1 py-0.5 text-[10px] font-semibold ${
                          isSelected ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        DEFAULT
                      </span>
                    )}
                  </div>
                  <div className={`text-xs ${isSelected ? 'text-slate-300' : 'text-slate-400'}`}>
                    {t.variants.length} variant{t.variants.length === 1 ? '' : 's'} · {t.status}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TemplatePanel(props: { templateId: string; kind: BrandKind; isDefault: boolean }) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ['brand-template', props.templateId],
    queryFn: () => getBrandTemplate(props.templateId),
    staleTime: 15_000,
  });

  const activeTab = KIND_TABS.find((t) => t.value === props.kind) ?? KIND_TABS[0];
  const canBeDefault = props.kind !== 'graphic';

  const setDefaultMut = useMutation({
    mutationFn: (id: string | null) =>
      putBrandDefaults({
        [activeTab.defaultsKey]: id,
      } as Partial<BrandDefaults>),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brand-defaults'] }),
  });

  if (detail.isLoading || !detail.data) {
    return <div className="p-4 text-sm text-slate-400">Loading template…</div>;
  }

  const { template, fields } = detail.data;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{template.name}</h2>
          {template.description && (
            <p className="mt-1 max-w-2xl text-sm text-slate-500">{template.description}</p>
          )}
          <div className="mt-2 text-xs text-slate-500">
            Available in:{' '}
            {template.variants.map((v) => (
              <span key={v.aspect} className="mr-1 rounded bg-slate-100 px-1 font-mono text-slate-700">
                {v.aspect}
              </span>
            ))}
          </div>
        </div>
        {canBeDefault && (
          <button
            className={`rounded px-3 py-1 text-xs font-semibold transition ${
              props.isDefault
                ? 'border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                : 'bg-slate-900 text-white hover:bg-slate-700'
            }`}
            disabled={setDefaultMut.isPending}
            onClick={() => setDefaultMut.mutate(props.isDefault ? null : template.id)}
          >
            {props.isDefault
              ? setDefaultMut.isPending
                ? 'Clearing…'
                : 'Clear default'
              : setDefaultMut.isPending
                ? 'Saving…'
                : `Set as default ${activeTab.label}`}
          </button>
        )}
      </div>

      <PreviewForm template={template} fields={fields} />
    </div>
  );
}

function PreviewForm(props: { template: BrandTemplate; fields: BrandField[] }) {
  const [aspect, setAspect] = useState<string>(props.template.variants[0]?.aspect ?? '16:9');
  const [matchID, setMatchID] = useState<string>('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [renderId, setRenderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const matches = useQuery({ queryKey: ['portal-matches-brand'], queryFn: () => listMatches() });

  useEffect(() => {
    setValues({});
    setRenderId(null);
  }, [props.template.id]);

  const startMut = useMutation({
    mutationFn: () =>
      queueBrandRender({
        template_id: props.template.id,
        aspect,
        output_kind:
          props.template.kind === 'intro' || props.template.kind === 'outro'
            ? 'mp4'
            : props.template.kind === 'thumbnail'
              ? 'jpg'
              : 'png',
        values,
        match_id: matchID || undefined,
      }),
    onSuccess: (r) => {
      setError(null);
      setRenderId(r.render_id);
    },
    onError: (e: Error) => setError(e.message),
  });

  const poll = useQuery({
    queryKey: ['brand-render', renderId],
    queryFn: () => (renderId ? getBrandRender(renderId) : Promise.resolve(null)),
    enabled: !!renderId,
    refetchInterval: (q) => (q.state.data?.status === 'ready' || q.state.data?.status === 'failed' ? false : 1000),
  });

  const outputURL = brandOutputURL(poll.data?.output_path);

  // Fields flagged with `auto_fill` are hidden if a match is selected -
  // the server will fill them from match/client sources.
  const visibleFields = useMemo(() => {
    if (!matchID) return props.fields;
    return props.fields.filter((f) => !(f.auto_fill && f.source !== 'custom'));
  }, [props.fields, matchID]);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-800">Preview</h3>
        <div>
          <label className="block text-xs font-semibold uppercase text-slate-500">Aspect</label>
          <select
            className="mt-1 w-40 rounded border border-slate-200 px-2 py-1 text-sm"
            value={aspect}
            onChange={(e) => setAspect(e.target.value)}
          >
            {props.template.variants.map((v) => (
              <option key={v.aspect} value={v.aspect}>
                {v.aspect} ({v.width}×{v.height})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase text-slate-500">
            Use match data
          </label>
          <select
            className="mt-1 w-full rounded border border-slate-200 px-2 py-1 text-sm"
            value={matchID}
            onChange={(e) => setMatchID(e.target.value)}
          >
            <option value="">— none (fill fields manually) —</option>
            {(matches.data ?? []).slice(0, 40).map((m: any) => (
              <option key={m.id} value={m.id}>
                {m.home} vs {m.away} ({m.scheduled_at?.slice(0, 10)})
              </option>
            ))}
          </select>
        </div>
        {visibleFields.length > 0 && (
          <div className="space-y-1.5">
            {visibleFields.map((f) => (
              <div key={f.key}>
                <label className="block text-xs text-slate-500">
                  <span className="font-mono">{f.key}</span> · {f.label}
                  {f.mandatory && <span className="ml-1 text-rose-600">*</span>}
                </label>
                {f.type === 'color' ? (
                  <input
                    type="color"
                    className="h-8 w-14 rounded border border-slate-200"
                    value={values[f.key] || '#000000'}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  />
                ) : (
                  <input
                    className="w-full rounded border border-slate-200 px-2 py-1 text-sm"
                    value={values[f.key] ?? ''}
                    placeholder={f.default_value || (f.source !== 'custom' ? `(${f.source})` : '')}
                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        <button
          className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-60"
          disabled={startMut.isPending}
          onClick={() => startMut.mutate()}
        >
          {startMut.isPending ? 'Queuing…' : 'Render preview'}
        </button>
        {error && <div className="text-xs text-rose-600">{error}</div>}
      </div>

      <div className="rounded border border-slate-200 bg-slate-50 p-3">
        {!renderId && (
          <div className="text-xs text-slate-400">
            The preview appears here once render finishes (usually a few seconds).
          </div>
        )}
        {renderId && poll.data && (
          <>
            <div className="text-xs text-slate-500">
              Status:{' '}
              <span
                className={
                  poll.data.status === 'ready'
                    ? 'font-semibold text-emerald-700'
                    : poll.data.status === 'failed'
                      ? 'font-semibold text-rose-700'
                      : 'font-semibold text-amber-700'
                }
              >
                {poll.data.status}
              </span>
            </div>
            {poll.data.status === 'failed' && (
              <div className="mt-2 text-xs text-rose-700">{poll.data.error}</div>
            )}
            {poll.data.status === 'ready' && outputURL && (
              <div className="mt-2">
                {poll.data.output_kind === 'mp4' ? (
                  <video controls src={outputURL} className="w-full rounded" />
                ) : (
                  <img src={outputURL} alt="preview" className="w-full rounded" />
                )}
                <a
                  href={outputURL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block text-xs text-slate-600 underline"
                >
                  Open in new tab
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default BrandStudioPage;
